import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { Person } from '../../../api/models/Person';
import { routes } from '../../../route-list';
import { history } from '../../../util/history';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { ViewPersonScreen } from '../components/ViewPersonScreen';
import { SelectItem } from '../../../api/models/SelectItem';
import { RhSelectOption } from '../../core/RhSelect';

export type ViewPersonProps = {
  user: Person | null;
  roles: string[] | null;
  churches: RhSelectOption[] | null;
  loading: boolean;
  formState: FormState;
  isCancelDisabled: boolean;
  isSaveDisabled: boolean;
  userUpdatedFormState: React.Dispatch<React.SetStateAction<FormState>>;
  userClickedCancel: () => void;
  userClickedSave: () => void;
  userClickedDeletePerson: () => void;
};

type FormState = {
  role: string;
  churchId: string;
  name: string;
};

const initialFormState: FormState = {
  role: '',
  churchId: '',
  name: '',
};

/**
 * Why is this not following the same structure as (link below)?
 * https://github.com/smashingboxes/arno/blob/dev/src/modules/home/containers/HomeContainer.tsx
 *
 * Reason 1: Don’t call Hooks inside loops, conditions, or nested functions
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
 * Reason 2: Don’t call Hooks from regular JavaScript functions.
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-from-react-functions)
 */
export const ViewPersonContainer = () => {
  const request = useRequest();
  const { id: userId } = useParams<{ id: string }>();

  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [currentUser, setCurrentUser] = useState<Person | null>(null);
  const [churches, setChurches] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { loading: isLoadingUser } = useAsyncRequest(
    useCallback(() => request<Person>(apiConfigs.getUserById(userId)), [request, userId]),
    useCallback(result => {
      const user = result as Person;
      setCurrentUser(user);
      setFormState(f => ({ ...f, role: user.role, churchId: user.churchId, name: user.name || '' }));
    }, [])
  );
  const { loading: isLoadingChurches } = useAsyncRequest(
    useCallback(() => request<any>(apiConfigs.getChurches()), [request, userId]),
    useCallback(result => {
      const churchesResult =
        result?.map((church: any) => ({
          label: church.name,
          value: church.id,
        })) || [];
      setChurches(churchesResult);
    }, [])
  );

  const { loading: isLoadingRoles, data: roles } = useAsyncRequest(
    useCallback(() => request<string[]>(apiConfigs.getRoles()), [request])
  );

  return (
    <ViewPersonScreen
      user={currentUser}
      roles={roles}
      churches={churches}
      loading={isLoadingUser || isLoadingRoles || isLoading || isLoadingChurches}
      formState={formState}
      isCancelDisabled={_.isEqual(
        { role: currentUser?.role, churchId: currentUser?.churchId, name: currentUser?.name || '' },
        { role: formState.role, churchId: formState.churchId, name: formState.name }
      )}
      isSaveDisabled={_.isEqual(
        { role: currentUser?.role, churchId: currentUser?.churchId, name: currentUser?.name || '' },
        { role: formState.role, churchId: formState.churchId, name: formState.name }
      )}
      userClickedCancel={() => {
        setFormState({
          role: currentUser?.role || '',
          churchId: currentUser?.churchId || '',
          name: currentUser?.name || '',
        });
      }}
      userUpdatedFormState={setFormState}
      userClickedSave={async () => {
        try {
          if (!currentUser) {
            throw new Error('Failed to save changes!');
          }

          setIsLoading(true);
          await request(
            apiConfigs.updateUserById(userId, {
              email: currentUser.email,
              externalId: currentUser.externalId,
              imagePath: currentUser.imagePath,
              name: formState.name,
              role: formState.role,
              churchId: formState.churchId,
            })
          );
          const updatedUser = await request<Person>(apiConfigs.getUserById(userId));
          setCurrentUser(updatedUser);
          setIsLoading(false);
          toast.success('User updated!');
        } catch (e: any) {
          toast.error(e.message || 'Failed to save changes!');
          setIsLoading(false);
        }
      }}
      userClickedDeletePerson={() => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this user?"
              onClose={options.onClose}
              onOk={async () => {
                options.onClose();
                try {
                  setIsLoading(true);
                  await request(apiConfigs.deleteUser(userId));
                  toast.success('User deleted!');
                  history.replace(routes.PEOPLE);
                } catch (e: any) {
                  toast.error(e.message || 'Failed to delete user');
                } finally {
                  setIsLoading(false);
                }
              }}
            />
          ),
        });
      }}
    />
  );
};
